import { CommonModule } from '@angular/common';
import { Component, DestroyRef, forwardRef, inject, Input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  ControlValueAccessor,
  FormArray,
  FormControl,
  FormGroup,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { IonButton, IonIcon, IonList, IonSegment, IonSegmentButton } from '@ionic/angular';
import { tap } from 'rxjs';

import { BottomSheetDirective, TextareaComponent } from '@ninety/mobile/platform/components';
import { UserNamePipe } from '@ninety/mobile/platform/services';

import {
  DiscussionCommitmentRating,
  DiscussionForm,
  DiscussionModel,
  YesNoMaybe,
} from '../../../models/discussion.models';
import { DiscussionCommitmentsFormValue, DiscussionFormMode } from '../../../models/form/discussion-form.model';
import { DiscussionFormBase } from '../discussion-form-base/discussion-form-base';

@Component({
  selector: 'nty-commitments-form',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    IonButton,
    IonIcon,
    IonList,
    IonSegment,
    IonSegmentButton,
    UserNamePipe,
    BottomSheetDirective,
    TextareaComponent,
  ],
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => DiscussionCommitmentsFormComponent), multi: true },
  ],
  templateUrl: 'discussion-commitments-form.component.html',
  styleUrls: ['../../discussion-form-container/common.scss'],
  styles: [
    `
      @use 'terra';

      .commitment-title-with-tooltip-container {
        display: flex;
        gap: 9px;
        align-items: center;

        * {
          margin: 0;
        }
      }

      .commitment-tooltip-list {
        color: terra.color(text);
        padding: 0 20px;
      }

      ion-button.tooltip-btn {
        --border-color: #{terra.color(text, lightest)};
        --border-radius: 100%;
        color: terra.color(text, lightest);

        display: flex;
        align-items: center;

        &::part(native) {
          min-width: 16px;
          min-height: 16px;
          max-width: 16px;
          max-height: 16px;
        }
      }

      .tooltip-content-heading {
        color: terra.color(text);
        margin-bottom: 22px;
        margin-top: 24px;
      }
    `,
  ],
})
export class DiscussionCommitmentsFormComponent extends DiscussionFormBase implements OnInit, ControlValueAccessor {
  private destroyRef = inject(DestroyRef);
  private managerValue: DiscussionCommitmentRating[] = [];
  private manageeValue: DiscussionCommitmentRating[] = [];

  @Input({ required: true }) discussion!: DiscussionModel;
  @Input() mode: DiscussionFormMode = DiscussionFormMode.Meeting;
  @Input() type: keyof Pick<DiscussionForm, 'leadershipCommitments' | 'coachingCommitments'> = 'leadershipCommitments';

  CommitmentRating = YesNoMaybe;

  form = new FormGroup({
    manager: new FormGroup({
      ratings: new FormArray<FormControl<YesNoMaybe>>([], { validators: [Validators.required] }),
      notes: new FormControl<string>('', { nonNullable: true }),
    }),
    managee: new FormGroup({
      ratings: new FormArray<FormControl<YesNoMaybe>>([], { validators: [Validators.required] }),
      notes: new FormControl<string>('', { nonNullable: true }),
    }),
  });

  ngOnInit() {
    this.form.valueChanges
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(() => {
          if (this.onChange) this.onChange(this.buildChangeResult());
          if (this.onTouched) this.onTouched();
        })
      )
      .subscribe();
  }

  writeValue(val: DiscussionCommitmentsFormValue): void {
    this.managerValue = val.manager.ratings;
    this.manageeValue = val.managee.ratings;

    this.form.reset();
    this.form.controls.manager.controls.notes.patchValue(val.manager.notes);
    this.form.controls.managee.controls.notes.patchValue(val.managee.notes);

    val.manager.ratings.forEach(({ rating }) =>
      this.form.controls.manager.controls.ratings.push(
        new FormControl(rating, { nonNullable: true, validators: [Validators.required] })
      )
    );

    val.managee.ratings.forEach(({ rating }) =>
      this.form.controls.managee.controls.ratings.push(
        new FormControl(rating, { nonNullable: true, validators: [Validators.required] })
      )
    );
  }

  setDisabledState?(isDisabled: boolean): void {
    if (isDisabled) {
      this.form.disable();
    } else {
      this.form.enable();
    }

    if (this.mode === DiscussionFormMode.Manager) {
      this.form.controls.managee.disable();
    }

    if (this.mode === DiscussionFormMode.Managee) {
      this.form.controls.manager.disable();
    }
  }

  private buildChangeResult(): DiscussionCommitmentsFormValue {
    return {
      manager: {
        ratings: this.managerRatingsCtrls.map((rating, idx) => ({
          ...this.managerValue[idx],
          rating: rating.value,
        })),
        notes: this.form.controls.manager.controls.notes.value ?? '',
      },
      managee: {
        ratings: this.manageeRatingsCtrls.map((rating, idx) => ({
          ...this.manageeValue[idx],
          rating: rating.value,
        })),
        notes: this.form.controls.managee.controls.notes.value ?? '',
      },
    };
  }

  get managerRatingsCtrls() {
    return this.form.controls.manager.controls.ratings.controls;
  }

  get manageeRatingsCtrls() {
    return this.form.controls.managee.controls.ratings.controls;
  }

  get sectionTitle() {
    return this.type === 'leadershipCommitments'
      ? (this.discussion.conversationLanguage?.leadershipCommitments ?? 'Leadership Commitments')
      : (this.discussion.conversationLanguage?.coachingCommitments ?? 'Coaching Commitments');
  }

  getCommitmentTitle(idx: number) {
    return this.mode === DiscussionFormMode.Manager
      ? this.discussion.managerDiscussionForm?.[this.type][idx].commitment.manager.title
      : this.discussion.managerDiscussionForm?.[this.type][idx].commitment.managee.title;
  }

  getCommitmentItems(idx: number) {
    return this.mode === DiscussionFormMode.Manager
      ? this.discussion.managerDiscussionForm?.[this.type][idx].commitment.manager.items
      : this.discussion.managerDiscussionForm?.[this.type][idx].commitment.managee.items;
  }
}
